import React, { useCallback, useState } from 'react';

import { Select } from '@aline/common';
import type { UUID } from '@aline/common';
import { isEmpty } from 'ramda';
import { AssignRoleList } from 'src/components/AssignRoleList';
import type { UserDefinedRoleIdToDetails } from 'src/components/AssignRoleList';
import { AlineButton } from 'src/components/generic/AlineButton';
import { AlineDialog } from 'src/components/generic/AlineDialog';
import { Apollo } from 'src/gql/generated';
import { useNotificationDispatch } from 'src/hooks';
import { useGetUnassignedWorkflowPermissions } from 'src/hooks/assignment';
import { NotificationsActionType } from 'src/notifications';
import { makeStyles } from 'tss-react/mui';
import { v4 as uuid4 } from 'uuid';

interface AssignRolesDialogProps {
  onClose: () => void;
  open: boolean;
  title: string;
  workflowId: UUID;
  /** Only these roles, such as the ones on one task. Every unfilled role when omitted. */
  roleIds?: readonly UUID[];
}

const useStyles = makeStyles()(theme => ({
  buttonRow: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(2),
    justifyContent: 'flex-end',
    padding: theme.spacing(2, 0, 0, 0),
  },
  dialogPaper: {
    width: theme.dialogStyles.mediumDialogWidth,
  },
}));

/**
 * Fill a workflow's unfilled roles with people. Each role is filled on its permission, so every
 * task and signature step that names the role picks up the person.
 */
export const AssignRolesDialog = React.memo(function AssignRolesDialog({
  onClose,
  open,
  roleIds,
  title,
  workflowId,
}: AssignRolesDialogProps) {
  const { classes } = useStyles();
  const dispatch = useNotificationDispatch();
  const [isSubmitting, setSubmitting] = useState<boolean>(false);
  const [assignmentMap, setAssignmentMap] = useState<UserDefinedRoleIdToDetails>({});
  const [changeCollaboratorAssignment] = Apollo.useChangeCollaboratorAssignmentMutation();
  const { unassignedWorkflowPermissions } = useGetUnassignedWorkflowPermissions({ workflowId });

  const handleClose = useCallback(() => {
    setAssignmentMap({});
    onClose();
  }, [onClose]);

  const handleAssign = useCallback(async () => {
    setSubmitting(true);
    await Promise.all(
      Object.entries(assignmentMap).map(async ([assignedRoleId, details]) => {
        const { email, userId: assignedUserId } = details;
        const permissionId = unassignedWorkflowPermissions.find(
          permission => permission.role?.id === assignedRoleId
        )?.id;
        if (!permissionId) {
          dispatch({
            payload: {
              key: uuid4(),
              message: 'Unable to locate permission for updating placeholder.',
              variant: 'error',
            },
            type: NotificationsActionType.PUSH_NOTIFICATION,
          });
          return;
        }

        await changeCollaboratorAssignment({
          variables: { input: { assignedRoleId, assignedUserId, email, permissionId } },
        });
      })
    );
    setSubmitting(false);
    handleClose();
  }, [
    assignmentMap,
    changeCollaboratorAssignment,
    dispatch,
    handleClose,
    unassignedWorkflowPermissions,
  ]);

  return (
    <AlineDialog
      classes={{ paper: classes.dialogPaper }}
      data-cy={Select.document.s4s.bottomBar.sanityCheck.unassignedRoles.container}
      open={open}
      title={title}
      onClose={handleClose}
    >
      <AssignRoleList
        entityId={workflowId}
        entityType="Workflow"
        filterBy="unassigned"
        roleIds={roleIds}
        onListChange={setAssignmentMap}
      />
      <div className={classes.buttonRow}>
        <AlineButton
          color="primary"
          data-cy={Select.document.s4s.bottomBar.sanityCheck.unassignedRoles.cancelButton}
          disabled={isSubmitting}
          variant="outlined"
          onClick={handleClose}
        >
          Cancel
        </AlineButton>
        <AlineButton
          color="primary"
          data-cy={Select.document.s4s.bottomBar.sanityCheck.unassignedRoles.nextButton}
          disabled={isEmpty(assignmentMap)}
          loading={isSubmitting}
          variant="contained"
          onClick={handleAssign}
        >
          Assign
        </AlineButton>
      </div>
    </AlineDialog>
  );
});
